// (C) Copyright 2025 Hewlett Packard Enterprise Development LP
import lodashGet from 'lodash/get'

import { validateForm } from '../../../../../../../utils/validation-utils'
import { oidcauthorization, ssoModeEnum } from '../../../../constants'
import { isMSP, isV2 } from '../../../../../../../utils/feature-flag-utils'

function validateField(fieldValue, requiredString, errorString, testFunction) {
  if (!fieldValue) return requiredString
  if (!testFunction(fieldValue)) return errorString
  return ''
}

function validateConfirmation(value, requiredString) {
  return value && value === 'CHANGE' ? '' : requiredString
}

function validateConfirmationSmalOidc(value, requiredString) {
  return value ? '' : requiredString
}
function validateDomains(value, requiredString) {
  return value ? '' : requiredString
}

export const validateGeneralFields = (formValues, t) => {
  const requiredString = t('iam:sso_profiles.view_edit_create.required')
  const errorString = t('iam:sso_profiles.view_edit_create.invalid_char')
  const userInput = new RegExp(/^[^<>(){}]*$/)
  const profileName = lodashGet(formValues, 'general.profileName')
  const ccsAttribute = lodashGet(formValues, 'attribute_mapping.ccsAttribute')
  const mspoption = lodashGet(formValues, 'msp_methods')
  const mspValueEdit = lodashGet(formValues, 'general.msp_methods')
  const authnoptions = lodashGet(formValues, 'general.Authnoptions')
  const authMethodChangeConfirmation = lodashGet(
    formValues,
    'general.authMethodChangeConfirmation'
  )
  const domain = lodashGet(formValues, 'domain')
  const glcpOidcFlag = lodashGet(formValues, 'glcpOidcFlag')
  const isdomainSelected = glcpOidcFlag && domain === undefined
  const isMspOptionSelected =
    domain && domain !== 'none'
      ? !mspoption?.length > 0 && !mspValueEdit
      : false
  const isV2Msp = isV2() && isMSP()

  const authorizationMethod = lodashGet(
    formValues,
    'general.authorizationMethod'
  )

  const oidauthorizationMethod = lodashGet(
    formValues,
    'general.oidauthorizationMethod'
  )

  const currentAuthorizationMethod = lodashGet(
    formValues,
    'general.currentAuthorizationMethod'
  )

  const currentOidcAuthorizationMethod = lodashGet(
    formValues,
    'general.currentOidcAuthorizationMethod'
  )

  let valid = userInput.test(profileName) && profileName

  if (isV2Msp && isMspOptionSelected) {
    valid = false
  }
  if (glcpOidcFlag && authnoptions === undefined) {
    valid = false
  }
  if (isdomainSelected) {
    valid = false
  }
  if (lodashGet(formValues, 'domain')) {
    if (
      authorizationMethod === ssoModeEnum.AUTHORIZATION ||
      oidauthorizationMethod === oidcauthorization.SSO
    ) {
      valid = valid && userInput.test(ccsAttribute) && ccsAttribute
    }

    if (
      formValues.currentDomain &&
      (currentAuthorizationMethod || currentOidcAuthorizationMethod) &&
      (authorizationMethod !== currentAuthorizationMethod ||
        oidauthorizationMethod !== currentOidcAuthorizationMethod)
    ) {
      valid =
        valid &&
        authMethodChangeConfirmation &&
        authMethodChangeConfirmation === 'CHANGE'

      if (
        (currentAuthorizationMethod === ssoModeEnum.AUTHORIZATION ||
          currentOidcAuthorizationMethod === oidcauthorization.SSO) &&
        !lodashGet(formValues, 'general.usersDisposition')
      ) {
        valid = false
      }
    }
  }

  return {
    profileName: validateField(
      profileName,
      requiredString,
      errorString,
      userInput.test.bind(userInput)
    ),
    ccsAttribute: validateField(
      ccsAttribute,
      requiredString,
      errorString,
      userInput.test.bind(userInput)
    ),
    authMethodChangeConfirmation: validateConfirmation(
      authMethodChangeConfirmation,
      requiredString
    ),
    mspoption: validateConfirmationSmalOidc(mspoption, requiredString),
    authnoptions: validateConfirmationSmalOidc(authnoptions, requiredString),
    domains: validateDomains(domain, requiredString),
    disposition: lodashGet(formValues, 'general.usersDisposition')
      ? ''
      : requiredString,
    isValid: valid
  }
}

export const validateOIDCMetadata = (formValues, t) => {
  const issuerUrl = lodashGet(formValues, 'oidcIdpConfig.issuer', '')
  const authorizationEndpoint = lodashGet(
    formValues,
    'oidcIdpConfig.authorizationEndpoint',
    ''
  )
  const tokenEndpoint = lodashGet(formValues, 'oidcIdpConfig.tokenEndpoint', '')
  const jwksEndpoint = lodashGet(formValues, 'oidcIdpConfig.jwksEndpoint', '')
  const userinfoEndpoint = lodashGet(
    formValues,
    'oidcIdpConfig.userInfoEndpoint',
    ''
  )
  const oidcConfig = lodashGet(formValues, 'oidcIdpConfig', {})

  // Check for empty or undefined values
  if (
    !oidcConfig ||
    !issuerUrl ||
    !authorizationEndpoint ||
    !tokenEndpoint ||
    !jwksEndpoint ||
    !userinfoEndpoint
  ) {
    return {
      isValid: false,
      message: t('authn:required')
    }
  }

  // Validate oidc endpoints
  if (
    !issuerUrl.startsWith('https://') ||
    !authorizationEndpoint.startsWith('https://') ||
    !tokenEndpoint.startsWith('https://') ||
    !userinfoEndpoint.startsWith('https://')
  ) {
    return {
      isValid: false,
      message: 'Invalid endpoint entry.'
    }
  }
  return {
    isValid: true,
    message: ''
  }
}

export const validateClientFields = (formValues, setFormError) => {
  const clientId = lodashGet(formValues, 'oidcIdpConfig.clientId', '').trim()
  const clientSecret = lodashGet(
    formValues,
    'oidcIdpConfig.clientSecret',
    ''
  ).trim()

  let hasError = false

  if (!clientId && !clientSecret) {
    setFormError((prevErrors) => ({
      ...prevErrors,
      clientId: 'Client ID and Client Secret are both required.'
    }))
    hasError = true
  }

  if (!clientId) {
    setFormError((prevErrors) => ({
      ...prevErrors,
      clientId: 'Client ID is required.'
    }))
    hasError = true
  }

  if (!clientSecret) {
    setFormError((prevErrors) => ({
      ...prevErrors,
      clientSecret: 'Client Secret is required.'
    }))
    hasError = true
  }

  if (hasError) {
    throw new Error('Validation failed for Client ID or Client Secret.')
  }

  return true
}

export const validateConfigureSettingsFields = (formValues, t) => {
  const requiredString = t('iam:sso_profiles.view_edit_create.required')
  const errorString = t('iam:sso_profiles.view_edit_create.invalid_char')
  const userInput = new RegExp(/^[^<>(){}]*$/)
  // const countryCode = lodashGet(formValues, 'attribute_mapping.countryCode')
  const lastName = lodashGet(formValues, 'attribute_mapping.lastName')
  const firstName = lodashGet(formValues, 'attribute_mapping.firstName')
  const email = lodashGet(formValues, 'attribute_mapping.email')

  const userInputValidate =
    userInput.test(email) &&
    userInput.test(lastName) &&
    userInput.test(firstName)

  const valid = userInputValidate && firstName && lastName && email
  return {
    // countryCode:
    //   (countryCode ? '' : requiredString) ||
    //   (userInput.test(countryCode) ? '' : errorString),
    email:
      (email ? '' : requiredString) ||
      (userInput.test(email) ? '' : errorString),
    lastName:
      (lastName ? '' : requiredString) ||
      (userInput.test(lastName) ? '' : errorString),
    firstName:
      (firstName ? '' : requiredString) ||
      (userInput.test(firstName) ? '' : errorString),
    isValid: valid
  }
}

export const validateConfigureSettings = (formValues, t) => {
  return validateForm(formValues, t, validateConfigureSettingsFields)
}

export const validateFields = (formValues, t) => {
  const configOptionValidate =
    lodashGet(formValues, 'saml_idp_config.config_option') !== ''
  if (!configOptionValidate) {
    return {
      isValid: false,
      configOptions: t('iam:sso_profiles.view_edit_create.required')
    }
  }

  const urlValidate = new RegExp(/^(http|https):\/\/[^ "]+$/)
  const userInput = new RegExp(/^[^<>(){}]*$/)
  const errorString = t('iam:sso_profiles.view_edit_create.invalid_char')
  const requiredString = t('iam:sso_profiles.view_edit_create.required')

  const logoutUrl = lodashGet(formValues, 'saml_idp_config.logout_url').trim()
  const entityId = lodashGet(formValues, 'saml_idp_config.entity_id').trim()
  const loginUrl = lodashGet(formValues, 'saml_idp_config.login_url').trim()
  const signingCert = lodashGet(
    formValues,
    'saml_idp_config.signing_certificate'
  ).trim()
  const userInputValidate =
    urlValidate.test(logoutUrl) &&
    urlValidate.test(loginUrl) &&
    userInput.test(entityId) &&
    userInput.test(logoutUrl) &&
    userInput.test(loginUrl) &&
    userInput.test(signingCert)
  const metadataUrl = lodashGet(
    formValues,
    'saml_idp_config.metadata_url'
  ).trim()
  const formValid =
    userInputValidate && entityId && loginUrl && logoutUrl && signingCert
  return {
    entityId:
      (entityId ? '' : requiredString) ||
      (userInput.test(entityId) ? '' : errorString),
    signingCert:
      (signingCert ? '' : requiredString) ||
      (userInput.test(signingCert) ? '' : errorString),
    loginUrl:
      (loginUrl ? '' : requiredString) ||
      (userInput.test(loginUrl) ? '' : errorString) ||
      (urlValidate.test(loginUrl)
        ? ''
        : t('iam:sso_profiles.view_edit_create.invalid_url')),
    logoutUrl:
      (logoutUrl ? '' : requiredString) ||
      (userInput.test(logoutUrl) ? '' : errorString) ||
      (urlValidate.test(logoutUrl)
        ? ''
        : t('iam:sso_profiles.view_edit_create.invalid_url')),
    uploadFile: formValid ? '' : requiredString,
    validateUrl: formValid ? '' : requiredString,
    metadataFile: formValid ? '' : requiredString,
    metadataUrl: metadataUrl ? '' : requiredString,
    isValid: !!formValid
  }
}

export const validateXMLFile = (file) => {
  const allowedExtensions = /(\.xml)$/i
  if (!allowedExtensions.exec(file.name)) {
    return false
  }
  return true
}

export const validateIDPDetails = (formValues, t) => {
  return validateForm(formValues, t, validateFields)
}

export const validateTimeoutField = (formValues, t) => {
  const minTimeout = 6
  const maxTimeout = 1440
  const timeout = lodashGet(formValues, 'timeout')

  const timeoutNumber = Number(timeout)
  const isNumberValid =
    !Number.isNaN(timeoutNumber) &&
    timeoutNumber >= minTimeout &&
    timeoutNumber <= maxTimeout

  let result
  if (isNumberValid) {
    result = ''
  } else {
    result = timeout
      ? t('iam:sso_profiles.view_edit_create.invalid_session_timeout', {
          minTimeout,
          maxTimeout
        })
      : t('iam:sso_profiles.view_edit_create.required')
  }

  return {
    timeout: result,
    isValid: isNumberValid
  }
}

export const validateRecoveryPassword = (password, username) => {
  const passwordRegex = new RegExp(
    /^(?=.{8,}$)(?=.*?[a-z])(?=.*?[A-Z])(?=.*?[0-9])(?=\w*[\W_]).*$/
  )
  return passwordRegex.test(password) && !password.includes(username)
}

export const validateRecoveryUser = (formValues, t) => {
  let passwordValid = true
  if (!lodashGet(formValues, 'recovery_user.skipPasswordValidation')) {
    passwordValid = validateRecoveryPassword(
      lodashGet(formValues, 'recovery_user.recoveryPassword'),
      lodashGet(formValues, 'recovery_user.username')
    )
  }
  const emailRegex = RegExp('[^@ \\t\\r\\n]+@[^@ \\t\\r\\n]+\\.[^@ \\t\\r\\n]+') // NOSONAR
  const emailValid = emailRegex.test(
    lodashGet(formValues, 'recovery_user.pocEmail')
  )
  const formValid =
    !lodashGet(formValues, 'recovery_user.recoveryUserEnabled') ||
    (emailValid && passwordValid)
  return {
    pocEmail: emailValid
      ? ''
      : t('iam:sso_profiles.view_edit_create.invalid_email'),
    recoveryPassword: passwordValid
      ? ''
      : t('iam:sso_profiles.view_edit_create.invalid_password'),
    isValid: Boolean(formValid)
  }
}
